// packages/web/src/routes/_authed/admin/email-templates.$templateKey.tsx
import { useState } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useSuspenseQuery, type QueryClient } from "@tanstack/react-query";
import { AdminShell } from "@/components/layout/admin-shell";
import { splitName } from "@/lib/split-name";
import { appSidebarSections, appBottomNavItems } from "@/lib/app-sidebar";
import { emailTemplatesGetOptions } from "@/api/email-templates/query-options";
import { TemplateEditForm } from "@/components/admin/email-templates/template-edit-form";
import { TemplatePreviewPane } from "@/components/admin/email-templates/template-preview-pane";
import { ResetConfirmDialog } from "@/components/admin/email-templates/reset-confirm-dialog";
import { Button } from "@/components/ui/button";
import type { RoleKind } from "@/components/vroom/role-pill";

interface SessionUser {
  name: string;
  email: string;
  role?: RoleKind;
}

export const Route = createFileRoute("/_authed/admin/email-templates/$templateKey")({
  beforeLoad: ({ context }) => {
    const user = (context as { user?: { role?: string } }).user;
    if (user?.role !== "super_admin") throw redirect({ to: "/dashboard" });
  },
  loader: ({ context, params }) => {
    const qc = (context as { queryClient: QueryClient }).queryClient;
    return qc.ensureQueryData(emailTemplatesGetOptions(params.templateKey));
  },
  component: EmailTemplateEdit,
});

function EmailTemplateEdit() {
  const { t } = useTranslation();
  const { user: sessionUser } = Route.useRouteContext() as { user: SessionUser };
  const { firstName, lastName } = splitName(sessionUser.name);
  const { templateKey } = Route.useParams();

  const { data } = useSuspenseQuery(emailTemplatesGetOptions(templateKey));

  const [locale, setLocale] = useState<string>("nl");
  const [resetOpen, setResetOpen] = useState(false);

  const existingRow = data.localeRows.find((r) => r.locale === locale);

  const [draft, setDraft] = useState({
    subject: existingRow?.subject ?? data.defaultSubject,
    slots: { ...data.defaultSlots, ...existingRow?.slots },
  });

  return (
    <AdminShell
      user={{ firstName, lastName, role: "super_admin" }}
      section="admin"
      breadcrumb={
        <>
          {t("admin.email-templates.breadcrumb")} / {templateKey}
        </>
      }
      sidebarSections={appSidebarSections({ active: "email-templates", role: "super_admin" }, t)}
      bottomNavItems={appBottomNavItems(
        { section: "admin", active: "email-templates", role: "super_admin" },
        t,
      )}
    >
      <div className="px-4 py-4 md:px-6 md:py-6">
        <div className="mb-4 flex items-center justify-between gap-4 flex-wrap">
          <h1 className="font-mono text-base font-semibold text-ink-900">{templateKey}</h1>
          <div className="flex items-center gap-2">
            <div className="flex gap-1">
              {["nl", "dev"].map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => setLocale(l)}
                  className={`px-3 py-1 text-sm rounded border ${
                    locale === l
                      ? "bg-p-100 border-p-300 text-p-700 font-medium"
                      : "border-line text-ink-500 hover:bg-surface-1"
                  }`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            {existingRow && (
              <Button
                type="button"
                variant="danger-soft"
                size="compact"
                onClick={() => setResetOpen(true)}
              >
                Terugzetten op standaard
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TemplateEditForm
            templateKey={templateKey}
            locale={locale}
            slotKeys={data.slotKeys}
            variableKeys={data.variableKeys}
            defaultSubject={data.defaultSubject}
            defaultSlots={data.defaultSlots}
            existingRow={existingRow}
            onDraftChange={setDraft}
          />
          <TemplatePreviewPane
            templateKey={templateKey}
            locale={locale}
            draft={draft}
            sampleVars={data.sampleVars}
          />
        </div>
      </div>

      <ResetConfirmDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        templateKey={templateKey}
        locale={locale}
        onReset={() => {
          setDraft({
            subject: data.defaultSubject,
            slots: { ...data.defaultSlots },
          });
        }}
      />
    </AdminShell>
  );
}
